<script setup lang="ts">
import { ref, onMounted } from "vue";
import { useRouter } from "vue-router";

const router = useRouter();
const token = localStorage.getItem("rc_token") ?? "";
const userName = ref("");
const windowInfo = ref({ isOpen: false, start: "", end: "", daysLeft: 0, nextWindow: "" });
const groups = ref<any[]>([]);
const myGroups = ref<any[]>([]);
const loading = ref(false);
const errorMsg = ref("");

const filterStage = ref("all");
const filterPurpose = ref("all");
const filterRhythm = ref("all");
const filterTheme = ref("all");
const filterTimeSlot = ref("all");

const showDetail = ref(false);
const showCreate = ref(false);
const showMyGroups = ref(false);
const selectedGroup = ref<any>(null);

const createName = ref("");
const createStage = ref("");
const createPurpose = ref("");
const createRhythm = ref("");
const createTimeSlot = ref("灵活不固定");
const createThemes = ref<string[]>([]);
const createRegion = ref("不限");
const createMaxMembers = ref(6);
const createDesc = ref("");
const customPurpose = ref("");
const customRhythm = ref("");
const customTheme = ref("");
const customRegion = ref("");
const createError = ref("");

const stages = ["准备","初级","二级","已结业","不限"];
const purposes = ["认证冲刺","主题共读/共练","日常打卡陪伴","复训互助"];
const rhythms = ["每天","每周2-3次","每周1次","灵活不固定"];
const themes = ["非暴力沟通","三个位置","影子沟通","倾听与提问","微习惯设计/福格行为模型","觉察觉照","海宝脱敏法"];
const timeSlots = ["早上","上午","下午","晚上","灵活不固定"];

async function api(path: string, opts?: RequestInit): Promise<any> {
  const res = await fetch(path, { ...opts, headers: { ...opts?.headers, Authorization: `Bearer ${token}` } });
  return res.json();
}

async function loadGroups() {
  loading.value = true;
  const params = new URLSearchParams();
  if (filterStage.value !== "all") params.set("stage", filterStage.value);
  if (filterPurpose.value !== "all") params.set("purpose", filterPurpose.value);
  if (filterRhythm.value !== "all") params.set("rhythm", filterRhythm.value);
  if (filterTheme.value !== "all") params.set("theme", filterTheme.value);
  if (filterTimeSlot.value !== "all") params.set("timeSlot", filterTimeSlot.value);
  const qs = params.toString();
  const data = await api(`/api/groups${qs ? "?" + qs : ""}`);
  windowInfo.value = data.window ?? windowInfo.value;
  groups.value = data.groups ?? [];
  loading.value = false;
}

async function loadMyGroups() {
  try { const data = await api("/api/groups/mine/list"); myGroups.value = data.groups ?? []; } catch {}
}

async function openDetail(id: number) {
  const data = await api(`/api/groups/${id}`);
  selectedGroup.value = data;
  showDetail.value = true;
}

async function doJoin(groupId: number) {
  errorMsg.value = "";
  const result = await api(`/api/groups/${groupId}/join`, { method: "POST" });
  if (result.error) { errorMsg.value = result.error; return; }
  await loadGroups(); await loadMyGroups();
  if (showDetail.value) await openDetail(groupId);
}

async function doLeave(groupId: number) {
  const result = await api(`/api/groups/${groupId}/leave`, { method: "POST" });
  if (result.error) { errorMsg.value = result.error; return; }
  await loadGroups(); await loadMyGroups();
  if (showDetail.value) await openDetail(groupId);
}


async function doDisband(groupId: number) {
  if (!confirm('确定要解散这个小组吗？此操作不可撤销。')) return;
  const result = await api('/api/groups/' + groupId + '/disband', { method: 'POST' });
  if (result.error) { errorMsg.value = result.error; return; }
  showDetail.value = false;
  await loadGroups();
  await loadMyGroups();
}

async function doConfirmMatch(groupId: number) {
  const result = await api('/api/groups/' + groupId + '/confirm', { method: 'POST' });
  if (result.error) { errorMsg.value = result.error; return; }
  errorMsg.value = '✅ 已确认成组，开始学习！';
  setTimeout(() => { errorMsg.value = ''; }, 2000);
  await loadGroups();
  if (showDetail.value) await openDetail(groupId);
}

async function doCompleteMatch(groupId: number) {
  const fb = prompt('本次认证支持中，有什么想对运营说的？（选填）') || undefined;
  const result = await api('/api/groups/' + groupId + '/complete', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ feedback: fb })
  });
  if (result.error) { errorMsg.value = result.error; return; }
  errorMsg.value = '🎉 本周期已完成！';
  setTimeout(() => { errorMsg.value = ''; }, 2000);
  showDetail.value = false;
  await loadGroups();
  await loadMyGroups();
}

async function doCreate() {
  createError.value = "";
  if (!createName.value || !createStage.value || !createPurpose.value || !createRhythm.value) {
    createError.value = "请填写完整信息"; return;
  }
  const finalPurpose = createPurpose.value === '__custom__' ? customPurpose.value : createPurpose.value;
  const finalRhythm = createRhythm.value === '__custom__' ? customRhythm.value : createRhythm.value;
  const finalRegion = createRegion.value === '__custom__' ? customRegion.value : createRegion.value;
  const finalThemes = customTheme.value 
    ? [...createThemes.value, ...customTheme.value.split(/[,，]/).map((t: string) => t.trim()).filter(Boolean)]
    : createThemes.value;
  const result = await api("/api/groups", {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name: createName.value, stage: createStage.value, purpose: finalPurpose, rhythm: finalRhythm, timeSlot: createTimeSlot.value, themes: finalThemes, region: finalRegion, maxMembers: createMaxMembers.value, description: createDesc.value })
  });
  if (result.error) { createError.value = result.error; return; }
  showCreate.value = false; resetCreateForm();
  await loadGroups(); await loadMyGroups();
}

function resetCreateForm() {
  createName.value = ""; createStage.value = ""; createPurpose.value = ""; createRhythm.value = "";
  createTimeSlot.value = "灵活不固定";
  createThemes.value = []; createRegion.value = "不限"; createMaxMembers.value = 6; createDesc.value = ""; createError.value = "";
  customPurpose.value = ""; customRhythm.value = ""; customTheme.value = ""; customRegion.value = "";
}

function toggleTheme(t: string) {
  const idx = createThemes.value.indexOf(t);
  if (idx === -1) createThemes.value.push(t); else createThemes.value.splice(idx, 1);
}

async function copyWechat(wx: string) {
  try { await navigator.clipboard.writeText(wx); } catch {}
}

function setFilter(k: string, v: string) {
  if (k === "stage") { filterStage.value = v; } else if (k === "purpose") { filterPurpose.value = v; }
  else if (k === "rhythm") { filterRhythm.value = v; } else if (k === "theme") { filterTheme.value = v; }
  else if (k === "timeSlot") { filterTimeSlot.value = v; }
  loadGroups();
}

function daysUntil(dateStr: string): number {
  const d = new Date(dateStr);
  const now = new Date();
  return Math.max(0, Math.ceil((d.getTime() - now.getTime()) / 86400000));
}

// ── Profile edit ──
const showProfile = ref(false);
const editName = ref("");
const editWechat = ref("");
const editStage = ref("");
const profileSaving = ref(false);
const showPasswordChange = ref(false);
const oldPassword = ref("");
const newPassword = ref("");
const confirmPassword = ref("");
const passwordSaving = ref(false);
const passwordMsg = ref("");
const passwordOk = ref(false);

function openProfile() {
  const raw = localStorage.getItem("rc_user");
  if (raw) try {
    const u = JSON.parse(raw);
    editName.value = u.name || "";
    editWechat.value = u.wechat || "";
    editStage.value = u.stage || "";
  } catch {}
  showPasswordChange.value = false;
  oldPassword.value = "";
  newPassword.value = "";
  confirmPassword.value = "";
  passwordMsg.value = "";
  showProfile.value = true;
}

async function saveProfile() {
  profileSaving.value = true;
  try {
    const body: Record<string, string> = {};
    if (editName.value.trim()) body.name = editName.value.trim();
    if (editWechat.value.trim()) body.wechat = editWechat.value.trim();
    body.stage = editStage.value;
    const res = await fetch("/api/auth/profile", {
      method: "PUT",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(body)
    });
    const data = await res.json();
    if (!data.success) { alert(data.error || "保存失败"); profileSaving.value = false; return; }
    const raw = localStorage.getItem("rc_user");
    if (raw) {
      const u = JSON.parse(raw);
      if (data.user) {
        u.name = data.user.name;
        u.wechat = data.user.wechat;
        u.stage = data.user.stage;
      }
      localStorage.setItem("rc_user", JSON.stringify(u));
    }
    // Update displayed name and token if name changed (so subsequent API calls use new name)
    if (data.user?.name) {
      userName.value = data.user.name;
      localStorage.setItem("rc_user", JSON.stringify(data.user));
    }
    // If name changed, the backend returns a new token — save it
    if (data.token) {
      localStorage.setItem("rc_token", data.token);
    }
    showProfile.value = false;
  } catch { alert("系统繁忙"); }
  profileSaving.value = false;
}

async function doChangePassword() {
  passwordMsg.value = "";
  passwordOk.value = false;
  if (!oldPassword.value) { passwordMsg.value = "请输入原密码"; return; }
  if (!newPassword.value || newPassword.value.length < 4) { passwordMsg.value = "新密码至少4位"; return; }
  if (newPassword.value !== confirmPassword.value) { passwordMsg.value = "两次输入的新密码不一致"; return; }
  passwordSaving.value = true;
  try {
    const res = await fetch("/api/auth/password", {
      method: "PUT",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ oldPassword: oldPassword.value, newPassword: newPassword.value })
    });
    const data = await res.json();
    if (data.error) { passwordMsg.value = data.error; passwordSaving.value = false; return; }
    passwordMsg.value = "✅ 密码修改成功";
    passwordOk.value = true;
    oldPassword.value = "";
    newPassword.value = "";
    confirmPassword.value = "";
  } catch { passwordMsg.value = "系统繁忙，请稍后重试"; }
  passwordSaving.value = false;
}

function logout() {
  localStorage.removeItem("rc_token"); localStorage.removeItem("rc_user");
  router.replace({ name: "login" });
}

onMounted(async () => {
  const raw = localStorage.getItem("rc_user");
  if (raw) try { userName.value = JSON.parse(raw).name; } catch {}
  await loadGroups();
});
</script>

<template>
  <div class="phone-frame">
    <div class="app-shell">
      <!-- Top bar -->
      <header class="topbar">
        <span class="tb-logo">互助平台 · 自由共学</span>
        <div class="tb-right">
          <span class="profile-btn" @click="openProfile" title="编辑个人资料">✏️</span>
          <span class="tb-user" @click="openProfile">{{ userName }}</span>
          <button class="tb-logout" @click="logout">退出</button>
        </div>
      </header>

      <!-- Nav tabs -->
      <nav class="tab-bar">
        <button :class="['tab', { active: !showMyGroups && !showCreate && !showDetail }]" @click="showMyGroups = false; showCreate = false; showDetail = false">撮合广场</button>
        <button :class="['tab', { active: showMyGroups }]" @click="showMyGroups = true; showCreate = false; showDetail = false; loadMyGroups()">我的小组</button>
      </nav>

      <!-- Window banner -->
      <div :class="['window-banner', windowInfo.isOpen ? 'win-open' : 'win-closed']" v-if="!showCreate && !showDetail">
        <template v-if="windowInfo.isOpen">
          撮合窗口开放中 · {{ windowInfo.start }} ~ {{ windowInfo.end }} · 仅剩 {{ windowInfo.daysLeft }} 天
        </template>
        <template v-else>
          距下次撮合窗口开放还有 {{ daysUntil(windowInfo.nextWindow) }} 天 · {{ windowInfo.nextWindow }} 开放
        </template>
      </div>

      <!-- Error toast -->
      <div v-if="errorMsg" class="toast" @click="errorMsg = ''">{{ errorMsg }}</div>

      <!-- === PLAZA === -->
      <main class="content" v-if="!showMyGroups && !showCreate && !showDetail">
        <!-- Filters -->
        <div class="filters">
          <div class="filter-section">
            <span class="fs-label">学习阶段</span>
            <div class="chip-row">
              <button v-for="s in ['all',...stages]" :key="s" :class="['chip', { on: filterStage === s }]" @click="setFilter('stage',s)">{{ s === 'all' ? '全部' : s }}</button>
            </div>
          </div>
          <div class="filter-section">
            <span class="fs-label">组队目的</span>
            <div class="chip-row">
              <button v-for="p in ['all',...purposes]" :key="p" :class="['chip', { on: filterPurpose === p }]" @click="setFilter('purpose',p)">{{ p === 'all' ? '全部' : p }}</button>
            </div>
          </div>
          <div class="filter-section">
            <span class="fs-label">学习节奏</span>
            <div class="chip-row">
              <button v-for="r in ['all',...rhythms]" :key="r" :class="['chip', { on: filterRhythm === r }]" @click="setFilter('rhythm',r)">{{ r === 'all' ? '全部' : r }}</button>
            </div>
          </div>
          <div class="filter-section">
            <span class="fs-label">时间段</span>
            <div class="chip-row">
              <button v-for="ts in ['all',...timeSlots]" :key="ts" :class="['chip', { on: filterTimeSlot === ts }]" @click="setFilter('timeSlot',ts)">{{ ts === 'all' ? '全部' : ts }}</button>
            </div>
          </div>
          <div class="filter-section">
            <span class="fs-label">共读/共练主题</span>
            <div class="chip-row">
              <button v-for="t in ['all',...themes]" :key="t" :class="['chip', { on: filterTheme === t }]" @click="setFilter('theme',t)">{{ t === 'all' ? '全部' : t }}</button>
            </div>
          </div>
        </div>

        <div v-if="loading" class="empty-state">加载中...</div>
        <div v-else-if="groups.length === 0" class="empty-state">
          <div class="empty-icon">📭</div>
          <div>暂无招募中的小组</div>
          <div class="empty-sub">点击下方 + 创建一个吧</div>
        </div>

        <div v-else class="card-list">
          <div v-for="g in groups" :key="g.id" class="group-card" @click="openDetail(g.id)">
            <div class="card-header">
              <span class="card-name">{{ g.name }}</span>
              <span :class="['card-badge', g.status === 'active' ? 'badge-green' : 'badge-gray']">{{ g.status === 'active' ? '进行中' : g.status === 'recruiting' ? '招募中' : '已完成' }}</span>
            </div>
            <div class="card-tags">
              <span class="c-tag">{{ g.stage }}</span>
              <span class="c-tag">{{ g.purpose }}</span>
              <span class="c-tag">{{ g.rhythm }}</span>
              <span class="c-tag" v-if="g.timeSlot">{{ g.timeSlot }}</span>
              <span v-for="t in g.themes" :key="t" class="c-tag c-tag-theme">{{ t }}</span>
            </div>
            <div class="card-footer">
              <span>👤 {{ g.leader.name }}</span>
              <span>👥 {{ g.currentMembers }}/{{ g.maxMembers }}</span>
              <span v-if="g.region !== '不限'">📍 {{ g.region }}</span>
            </div>
          </div>
        </div>
      </main>

      <!-- FAB -->
      <button class="fab" @click="showCreate = true; showDetail = false" v-if="!showCreate && !showDetail">
        <span class="fab-icon">+</span>
        <span class="fab-text">创建</span>
      </button>

      <!-- === DETAIL === -->
      <main class="content" v-if="showDetail && selectedGroup">
        <button class="back-link" @click="showDetail = false">← 返回广场</button>
        <div class="detail-card">
          <div class="card-header">
            <span class="card-name">{{ selectedGroup.name }}</span>
            <span :class="['card-badge', selectedGroup.status === 'active' ? 'badge-green' : 'badge-gray']">{{ selectedGroup.status === 'active' ? '已成组' : '招募中' }}</span>
          </div>
          <div class="card-tags" style="margin-bottom:14px">
            <span class="c-tag">{{ selectedGroup.stage }}</span>
            <span class="c-tag">{{ selectedGroup.purpose }}</span>
            <span class="c-tag">{{ selectedGroup.rhythm }}</span>
            <span class="c-tag" v-if="selectedGroup.timeSlot">{{ selectedGroup.timeSlot }}</span>
            <span v-for="t in selectedGroup.themes" :key="t" class="c-tag c-tag-theme">{{ t }}</span>
          </div>
          <p class="desc-text" v-if="selectedGroup.description">{{ selectedGroup.description }}</p>
          <div class="leader-row"><span>👤 组长：{{ selectedGroup.leader.name }}</span><span v-if="selectedGroup.leader.wechat">💬 {{ selectedGroup.leader.wechat }} <button class="btn-copy" @click="copyWechat(selectedGroup.leader.wechat)">复制</button></span></div>
          <div class="meta-row">
            <span>👥 {{ selectedGroup.currentMembers }}/{{ selectedGroup.maxMembers }}人</span>
            <span v-if="selectedGroup.region !== '不限'">📍 {{ selectedGroup.region }}</span>
          </div>

          <h3 class="sec-title">小组成员 <span class="sec-count">{{ selectedGroup.members?.length || 0 }}人</span></h3>
          <div class="member-list">
            <div v-for="m in selectedGroup.members" :key="m.name" class="member-item">
              <span class="mem-avatar">{{ m.initial }}</span>
              <div class="mem-info">
                <span class="mem-name">{{ m.name }}<em v-if="m.role==='leader'" class="mem-badge-leader">组长</em></span>
                <span v-if="m.wechat" class="mem-wx">{{ m.wechat }}</span>
              </div>
              <button v-if="m.wechat" class="btn-copy" @click="copyWechat(m.wechat)">复制</button>
            </div>
          </div>

          <div class="action-row">
            <button v-if="selectedGroup.status !== 'ended' && selectedGroup.currentMembers < selectedGroup.maxMembers && !selectedGroup.members?.some((m:any) => m.name === userName)" class="btn-primary" @click="doJoin(selectedGroup.id)">加入小组</button>
            <button v-if="selectedGroup.members?.some((m:any) => m.name === userName && m.role !== 'leader')" class="btn-danger" @click="doLeave(selectedGroup.id)">退出小组</button>
            <div v-if="selectedGroup.members?.some((m:any) => m.name === userName && m.role === 'leader')" class="action-row">
              <button v-if="selectedGroup.status === 'recruiting'" class="btn-primary" @click="doConfirmMatch(selectedGroup.id)" style="background:#3d7a5a;">✅ 确认成组，停止招募</button>
              <button v-if="selectedGroup.status === 'active'" class="btn-primary" @click="doCompleteMatch(selectedGroup.id)" style="background:#e8894f;">🎉 本周期已完成</button>
              <button class="btn-danger" @click="doDisband(selectedGroup.id)">解散小组</button>
            </div>
          </div>
        </div>
      </main>

      <!-- === CREATE === -->
      <main class="content" v-if="showCreate">
        <button class="back-link" @click="showCreate = false; resetCreateForm()">← 返回</button>
        <div class="detail-card">
          <h2 class="dt-name">创建小组</h2>

          <label class="field-label">小组名称</label>
          <input v-model="createName" class="input" placeholder="给你的小组起个名字" maxlength="100" />

          <label class="field-label">学习阶段</label>
          <div class="chip-row"><button v-for="s in stages" :key="s" :class="['chip', { on: createStage === s }]" @click="createStage = s">{{ s }}</button></div>

          <label class="field-label">组队目的</label>
          <div class="chip-row"><button v-for="p in purposes" :key="p" :class="['chip', { on: createPurpose === p }]" @click="createPurpose = p">{{ p }}</button><button :class="['chip', { on: createPurpose === '__custom__' }]" @click="createPurpose = '__custom__'">✏️ 自定义</button></div>
          <input v-if="createPurpose === '__custom__'" v-model="customPurpose" class="input" placeholder="输入自定义组队目的" style="margin-top:4px" />

          <label class="field-label">学习节奏</label>
          <div class="chip-row"><button v-for="r in rhythms" :key="r" :class="['chip', { on: createRhythm === r }]" @click="createRhythm = r">{{ r }}</button><button :class="['chip', { on: createRhythm === '__custom__' }]" @click="createRhythm = '__custom__'">✏️ 自定义</button></div>
          <input v-if="createRhythm === '__custom__'" v-model="customRhythm" class="input" placeholder="输入自定义学习节奏" style="margin-top:4px" />

          <label class="field-label">时间段</label>
          <div class="chip-row"><button v-for="t in timeSlots" :key="t" :class="['chip', { on: createTimeSlot === t }]" @click="createTimeSlot = t">{{ t }}</button></div>

          <label class="field-label">共读/共练主题（可多选）</label>
          <div class="chip-row"><button v-for="t in themes" :key="t" :class="['chip', { on: createThemes.includes(t) }]" @click="toggleTheme(t)">{{ t }}</button></div>
          <input v-model="customTheme" class="input" placeholder="添加自定义主题（回车确认，多个用逗号分隔）" style="margin-top:4px" />

          <label class="field-label">地域</label>
          <div class="chip-row"><button v-for="r in ['不限','北京','上海','广东','浙江','江苏','四川','湖北','山东','福建']" :key="r" :class="['chip', { on: createRegion === r }]" @click="createRegion = r">{{ r }}</button><button :class="['chip', { on: createRegion === '__custom__' }]" @click="createRegion = '__custom__'">✏️ 自定义</button></div>
          <input v-if="createRegion === '__custom__'" v-model="customRegion" class="input" placeholder="输入城市/省份" style="margin-top:4px" />

          <label class="field-label">人数上限：<strong>{{ createMaxMembers }}人</strong></label>
          <input v-model.number="createMaxMembers" type="range" min="3" max="12" class="slider" />

          <label class="field-label">小组简介<span class="field-opt">（选填）</span></label>
          <textarea v-model="createDesc" class="input textarea" placeholder="介绍一下你的小组计划..." maxlength="300"></textarea>

          <p v-if="createError" class="err-text">{{ createError }}</p>
          <button class="btn-primary" @click="doCreate">创建小组</button>
        </div>
      </main>

      <!-- === MY GROUPS === -->
      <main class="content" v-if="showMyGroups && !showCreate && !showDetail">
        <div v-if="myGroups.length === 0" class="empty-state">
          <div class="empty-icon">📋</div>
          <div>你还没有加入或创建任何小组</div>
          <div class="empty-sub">去撮合广场看看吧</div>
        </div>
        <div v-else class="card-list">
          <div v-for="g in myGroups" :key="g.id" class="group-card" @click="showMyGroups = false; openDetail(g.id)">
            <div class="card-header">
              <span class="card-name">{{ g.name }}</span>
              <span :class="['card-badge', g.myRole === 'leader' ? 'badge-orange' : g.status === 'active' ? 'badge-green' : 'badge-gray']">{{ g.myRole === 'leader' ? '组长' : g.status === 'active' ? '进行中' : g.status === 'recruiting' ? '招募中' : '已完成' }}</span>
            </div>
            <div class="card-tags">
              <span class="c-tag">{{ g.stage }}</span>
              <span class="c-tag">{{ g.purpose }}</span>
              <span class="c-tag">{{ g.rhythm }}</span>
            </div>
            <div class="card-footer">
              <span>👥 {{ g.currentMembers }}/{{ g.maxMembers }}人</span>
              <span>{{ g.windowPeriod }}期</span>
            </div>
          </div>
        </div>
      </main>

    </div>
  </div>

  <!-- Profile edit overlay -->
  <div class="overlay" v-if="showProfile" @click.self="showProfile = false"></div>
  <div class="profile-panel" v-if="showProfile">
    <h3>编辑个人资料</h3>

    <label class="field-label">姓名</label>
    <input v-model="editName" class="input" placeholder="你的姓名" />

    <label class="field-label">微信号</label>
    <input v-model="editWechat" class="input" placeholder="用于组队联系" />

    <label class="field-label">学习阶段</label>
    <div class="chip-row">
      <button v-for="s in stages" :key="s" :class="['chip', { active: editStage === s }]" @click="editStage = s">{{ s }}</button>
    </div>

    <!-- Password change -->
    <div class="password-section">
      <div class="password-toggle" @click="showPasswordChange = !showPasswordChange">
        <span>{{ showPasswordChange ? '▼' : '▶' }} 修改密码</span>
      </div>
      <div v-if="showPasswordChange" class="password-fields">
        <input v-model="oldPassword" type="password" class="input" placeholder="原密码" />
        <input v-model="newPassword" type="password" class="input" placeholder="新密码（至少4位）" />
        <input v-model="confirmPassword" type="password" class="input" placeholder="确认新密码" />
        <button class="btn-save" :disabled="passwordSaving" @click="doChangePassword" style="width:100%;margin-top:4px;">
          {{ passwordSaving ? "修改中..." : "确认修改密码" }}
        </button>
        <div v-if="passwordMsg" :style="{color: passwordOk ? '#3d7a5a' : '#d4786e', fontSize:'12px', marginTop:'6px', textAlign:'center'}">{{ passwordMsg }}</div>
      </div>
    </div>

    <div class="profile-actions">
      <button class="btn-cancel" @click="showProfile = false">取消</button>
      <button class="btn-save" :disabled="profileSaving" @click="saveProfile">{{ profileSaving ? "保存中..." : "保存" }}</button>
    </div>
  </div>
</template>

<style>
/* Global reset for app */
* { margin: 0; padding: 0; box-sizing: border-box; }
body { background: #f5f0e8; }
</style>

<style scoped>
.phone-frame {
  max-width: 430px;
  margin: 0 auto;
  height: 100%;
  background: #fef9f2;
  box-shadow: 0 12px 40px rgba(61,53,41,0.14);
  position: relative;
  overflow: hidden;
}
.app-shell {
  height: 100%;
  display: flex;
  flex-direction: column;
  font-family: -apple-system, BlinkMacSystemFont, "PingFang SC", "Microsoft YaHei", sans-serif;
  color: #3d3529;
  -webkit-font-smoothing: antialiased;
}

/* Top bar */
.topbar {
  display: flex; align-items: center; justify-content: space-between;
  padding: 12px 16px; background: #fff;
  border-bottom: 1px solid #f0e8dd;
  position: sticky; top: 0; z-index: 10;
}
.tb-logo { font-weight: 700; font-size: 16px; color: #e8894f; }
.tb-right { display: flex; align-items: center; gap: 10px; }
.tb-user { font-size: 13px; color: #8b7e6a; }
.tb-logout { padding: 4px 10px; font-size: 11px; border: 1px solid #e8e4df; border-radius: 6px; background: none; color: #8b7e6a; cursor: pointer; }

/* Tab bar */
.tab-bar { display: flex; background: #fff; border-bottom: 1px solid #f0e8dd; }
.tab { flex: 1; padding: 14px 0; border: none; background: none; font-size: 15px; font-weight: 600; color: #b8a99a; cursor: pointer; position: relative; transition: color 0.2s; font-family: inherit; }
.tab.active { color: #e8894f; }
.tab.active::after { content: ''; position: absolute; bottom: 0; left: 20%; right: 20%; height: 3px; background: #e8894f; border-radius: 3px 3px 0 0; }

/* Window banner */
.window-banner { padding: 10px 16px; text-align: center; font-size: 12px; font-weight: 600; }
.win-open { background: #edf6f1; color: #3d7a5a; }
.win-closed { background: #fef9ee; color: #8a5c15; }

/* Toast */
.toast { position: fixed; top: 60px; left: 50%; transform: translateX(-50%); z-index: 99; background: #fdf3f1; color: #d4786e; padding: 10px 20px; border-radius: 10px; font-size: 13px; box-shadow: 0 4px 12px rgba(0,0,0,0.1); cursor: pointer; max-width: 380px; }

/* Content area */
.content { flex: 1; overflow-y: auto; padding-bottom: 80px; }

/* Filters */
.filters { background: #fff; padding: 12px 16px; border-bottom: 1px solid #f0ede9; }
.filter-section { margin-bottom: 10px; }
.filter-section:last-child { margin-bottom: 0; }
.fs-label { font-size: 11px; font-weight: 600; color: #b8a99a; display: block; margin-bottom: 6px; }
.chip-row { display: flex; gap: 6px; flex-wrap: wrap; }
.chip { padding: 5px 12px; border: 1px solid #e8e4df; border-radius: 14px; font-size: 12px; background: #fafaf8; color: #4a4744; cursor: pointer; white-space: nowrap; transition: all 0.15s; }
.chip.on { background: #e8894f; color: #fff; border-color: #e8894f; }

/* Cards */
.card-list { padding: 12px 16px; }
.group-card { background: #fff; border-radius: 14px; padding: 16px; margin-bottom: 10px; box-shadow: 0 1px 3px rgba(61,53,41,0.06); cursor: pointer; transition: transform 0.1s; }
.group-card:active { transform: scale(0.98); }
.card-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 8px; gap: 8px; }
.card-name { font-size: 15px; font-weight: 600; color: #3d3529; flex: 1; line-height: 1.4; }
.card-badge { font-size: 10px; padding: 2px 8px; border-radius: 10px; white-space: nowrap; flex-shrink: 0; }
.badge-gray { background: #f0ede9; color: #8b7e6a; }
.badge-green { background: #edf6f1; color: #3d7a5a; }
.badge-orange { background: #fef5ee; color: #e8894f; }
.card-tags { display: flex; gap: 4px; flex-wrap: wrap; margin-bottom: 8px; }
.c-tag { font-size: 10px; padding: 2px 8px; border-radius: 8px; background: #f0ede9; color: #8b7e6a; }
.c-tag-theme { background: #eef4fa; color: #6b8fbf; }
.card-footer { display: flex; gap: 12px; font-size: 11px; color: #b8a99a; }

/* Empty state */
.empty-state { text-align: center; padding: 60px 20px; color: #b8a99a; font-size: 14px; }
.empty-icon { font-size: 40px; margin-bottom: 10px; }
.empty-sub { font-size: 12px; color: #d4cbc0; margin-top: 4px; }

/* FAB */
.fab { position: fixed; bottom: calc(78px + env(safe-area-inset-bottom, 0px)); right: calc(50% - 215px + 20px); display: flex; align-items: center; gap: 6px; padding: 12px 20px; background: linear-gradient(135deg, #e8894f, #d4a843); color: #fff; border: none; border-radius: 24px; font-size: 14px; font-weight: 600; cursor: pointer; box-shadow: 0 4px 16px rgba(232,137,79,0.35); z-index: 40; transition: all 0.2s; }
.fab:active { transform: scale(0.95); }
.fab-icon { font-size: 20px; line-height: 1; }
.fab-text { font-size: 14px; }

/* Back link */
.back-link { display: inline-block; padding: 12px 16px 0; font-size: 14px; color: #e8894f; background: none; border: none; cursor: pointer; }

/* Detail card */
.detail-card { margin: 8px 16px; background: #fff; border-radius: 16px; padding: 20px; box-shadow: 0 1px 3px rgba(61,53,41,0.06); }
.dt-name { font-size: 20px; font-weight: 700; margin-bottom: 16px; }
.desc-text { font-size: 14px; color: #4a4744; line-height: 1.8; margin-bottom: 12px; }
.leader-row { display: flex; align-items: center; gap: 12px; font-size: 13px; color: #3d3529; margin-bottom: 8px; flex-wrap: wrap; }
.meta-row { display: flex; gap: 16px; font-size: 12px; color: #b8a99a; margin-bottom: 20px; }
.sec-title { font-size: 14px; font-weight: 600; margin-bottom: 12px; color: #3d3529; border-top: 1px solid #f0ede9; padding-top: 16px; }
.sec-count { font-weight: 400; color: #b8a99a; font-size: 12px; }

/* Members */
.member-list { }
.member-item { display: flex; align-items: center; gap: 10px; padding: 8px 0; border-bottom: 1px solid #fafaf8; gap: 10px; }
.mem-avatar { width: 32px; height: 32px; border-radius: 50%; background: #fef5ee; color: #e8894f; display: flex; align-items: center; justify-content: center; font-size: 13px; font-weight: 600; flex-shrink: 0; }
.mem-info { flex: 1; min-width: 0; }
.mem-name { font-size: 14px; display: flex; align-items: center; gap: 6px; }
.mem-wx { font-size: 11px; color: #8b7e6a; display: block; margin-top: 2px; }
.mem-badge-leader { font-style: normal; font-size: 9px; background: #fef5ee; color: #e8894f; padding: 1px 6px; border-radius: 6px; }
.btn-copy { padding: 4px 10px; font-size: 11px; border: 1px solid #e8e4df; border-radius: 6px; background: none; cursor: pointer; color: #8b7e6a; flex-shrink: 0; white-space: nowrap; }

/* Actions */
.action-row { margin-top: 20px; display: flex; flex-direction: column; gap: 8px; }
.hint-leader { font-size: 12px; color: #b8a99a; text-align: center; }

/* Buttons */
.btn-primary { width: 100%; padding: 13px; background: #e8894f; color: #fff; border: none; border-radius: 12px; font-size: 15px; font-weight: 600; cursor: pointer; }
.btn-primary:hover { background: #c96d35; }
.btn-danger { width: 100%; padding: 13px; background: #fff; color: #d4786e; border: 1px solid #d4786e; border-radius: 12px; font-size: 15px; font-weight: 600; cursor: pointer; }

/* Form */
.field-label { display: block; font-size: 13px; font-weight: 600; color: #3d3529; margin: 16px 0 6px; }
.field-opt { font-weight: 400; color: #b8a99a; font-size: 11px; }
.input { width: 100%; padding: 10px 12px; border: 1px solid #e8e4df; border-radius: 10px; font-size: 14px; background: #fafaf8; color: #3d3529; outline: none; }
.input:focus { border-color: #e8894f; }
.textarea { min-height: 80px; resize: vertical; }
.slider { width: 100%; accent-color: #e8894f; margin: 8px 0; }
.err-text { color: #d4786e; font-size: 13px; margin-top: 10px; padding: 8px 12px; background: #fdf3f1; border-radius: 8px; }

/* Profile panel */
.tb-user { cursor: pointer; }
.profile-btn {
  width: 28px; height: 28px; border-radius: 50%;
  border: 1.5px solid #c0835a; background: #fff;
  font-size: 13px; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: all 0.2s; line-height: 1;
}
.profile-btn:hover { background: #fdf6f0; transform: scale(1.05); }
.overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.3); z-index: 50; }
.profile-panel { position: fixed; top: 50%; left: 50%; transform: translate(-50%,-50%); background: #fff; border-radius: 16px; padding: 24px; width: calc(100% - 64px); max-width: 360px; z-index: 51; box-shadow: 0 8px 30px rgba(0,0,0,0.15); }
.profile-panel h3 { font-size: 17px; margin-bottom: 8px; color: #3d3529; }
.profile-actions { display: flex; gap: 10px; margin-top: 20px; }
.btn-cancel { flex: 1; padding: 10px; border: 1px solid #e8e4df; border-radius: 10px; background: #fff; color: #8b7e6a; font-size: 14px; cursor: pointer; }
.btn-save { flex: 1; padding: 10px; border: none; border-radius: 10px; background: #e8894f; color: #fff; font-size: 14px; font-weight: 600; cursor: pointer; }
.btn-save:disabled { background: #d4a58a; }
.password-section { margin-top: 16px; border-top: 1px solid #f0e8dd; padding-top: 14px; }
.password-toggle { font-size: 13px; color: #8b7e6a; cursor: pointer; user-select: none; padding: 6px 0; }
.password-toggle:hover { color: #5c5040; }
.password-fields { display: flex; flex-direction: column; gap: 8px; margin-top: 10px; }
.chip-row { display: flex; gap: 8px; flex-wrap: wrap; margin-top: 4px; }
.chip { padding: 6px 14px; border: 1px solid #e8e4df; border-radius: 16px; background: #fafaf8; font-size: 13px; color: #4a4744; cursor: pointer; }
.chip.active { background: #e8894f; color: #fff; border-color: #e8894f; }
</style>
